import React, { useMemo, useState } from 'react';
import { Snowflake, Search } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import {
  partidasParaEscolher, saldoProjetado, emDoses, SEM_DONO,
} from '../../../data/semen';
import { enderecoDoBotijao } from '../../../data/estoque';
import { MARK } from '../../../constants';

/**
 * Escolher, no botijão, as palhetas que esta FIV vai descongelar.
 *
 * Substitui o <select> por linha do bloco Sêmen da sessão. Ele listava o
 * laboratório inteiro numa frase só — "TOURO · partida · dono — 3 no estoque
 * (botijão…)" —, trinta e tantas opções de texto corrido, uma palheta por vez.
 *
 * Abre no dono que é o cliente do serviço, que é de onde o sêmen quase sempre
 * sai; os outros estão no seletor, como no Descongelar do estoque da TE. A
 * busca procura dentro do dono escolhido e, quando não acha ali, diz quantas
 * existem nos outros.
 *
 * Nada é gravado aqui. As escolhidas entram no bloco da sessão e só baixam o
 * estoque quando a FIV é salva.
 */
const SeletorDePalhetas = ({
  batches = [],
  fora = [],
  dosesGravadas = {},
  clientId = null,
  clientName = '',
  onEscolher,
  onFechar,
}) => {
  const disponiveis = useMemo(() => partidasParaEscolher(batches, { fora }), [batches, fora]);

  // Cliente sem partida no botijão: abrir vazio só faria procurar o seletor.
  const [dono, setDono] = useState(() =>
    (clientId && disponiveis.some(b => b.client_id === clientId) ? clientId : ''));
  const [busca, setBusca] = useState('');
  // semen_batch_id → doses
  const [sel, setSel] = useState({});

  const donos = useMemo(() => {
    const m = new Map();
    disponiveis.forEach(b => {
      const id = b.client_id || SEM_DONO;
      const d = m.get(id) || { id, nome: b.dono || 'Sem dono', n: 0 };
      d.n += 1;
      m.set(id, d);
    });
    return [...m.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt'));
  }, [disponiveis]);

  const visiveis = useMemo(
    () => partidasParaEscolher(disponiveis, { busca, dono }), [disponiveis, busca, dono]);
  const nosOutros = useMemo(
    () => (dono && busca.trim() && visiveis.length === 0
      ? partidasParaEscolher(disponiveis, { busca }).length : 0),
    [disponiveis, busca, dono, visiveis.length]);

  const alternar = (id) => setSel(s => {
    const n = { ...s };
    if (id in n) delete n[id]; else n[id] = 1;
    return n;
  });
  const mudarDoses = (id, v) => setSel(s => ({ ...s, [id]: v }));

  const escolhidas = Object.keys(sel);
  const totalDeDoses = escolhidas.reduce((t, id) => t + (Number(sel[id]) || 0), 0);

  const usar = () => {
    onEscolher(escolhidas.map(id => ({
      semen_batch_id: id, doses: Number(sel[id]) || 0, observacoes: '',
    })));
    onFechar();
  };

  return (
    <JanelaModal chave="seletor-de-palhetas" camada="z-[60]" largura="sm:max-w-2xl"
      Icone={Snowflake}
      titulo="Escolher palhetas"
      subtitulo="As escolhidas baixam do estoque quando a FIV é salva."
      onFechar={onFechar}>
      <div className="p-4 pb-2 space-y-2 border-b border-slate-100">
        <label className="flex items-center gap-2 px-2 py-1.5 border border-slate-300 rounded-lg
                          focus-within:ring-1 focus-within:ring-blue-400">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            autoFocus
            type="text"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Touro, partida, dono ou botijão…"
            className="flex-1 min-w-0 text-sm focus:outline-none"
          />
        </label>
        {donos.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            Dono
            <select value={dono} onChange={e => setDono(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1 text-sm text-slate-700">
              {clientId && !donos.some(d => d.id === clientId) && (
                <option value={clientId}>{clientName || 'Este cliente'} (0)</option>
              )}
              {donos.map(d => (
                <option key={d.id} value={d.id}>
                  {d.nome}{d.id === clientId ? ' — este serviço' : ''} ({d.n})
                </option>
              ))}
              <option value="">Todos os donos ({disponiveis.length})</option>
            </select>
          </label>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2">
        {disponiveis.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">
            Não há partida com saldo no botijão. A entrada de sêmen é feita no Estoque.
          </p>
        )}
        {disponiveis.length > 0 && visiveis.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">
            {busca.trim() ? 'Nenhuma partida com essa busca.' : 'Este dono não tem partida com saldo.'}
            {(nosOutros > 0 || (!busca.trim() && dono)) && (
              <button type="button" onClick={() => setDono('')}
                className="block mx-auto mt-2 text-blue-600 hover:underline">
                {nosOutros > 0 ? `Ver ${nosOutros} em outros donos` : 'Ver todos os donos'}
              </button>
            )}
          </div>
        )}

        {visiveis.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-slate-500 border-b border-slate-200 text-xs">
              <tr>
                <th className="w-8"></th>
                <th className="px-2 py-1.5 text-left font-medium">Touro</th>
                <th className="px-2 py-1.5 text-left font-medium">Partida</th>
                {!dono && <th className="px-2 py-1.5 text-left font-medium">Dono</th>}
                <th className="px-2 py-1.5 text-left font-medium">Onde está</th>
                <th className="px-2 py-1.5 text-right font-medium">Estoque</th>
                <th className="px-2 py-1.5 text-center font-medium w-36">Doses</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visiveis.map(b => {
                const id = b.semen_batch_id;
                const marcada = id in sel;
                const restante = saldoProjetado(b.saldo_doses, dosesGravadas[id] || 0, marcada ? sel[id] : 0);
                return (
                  <tr key={id} onClick={() => alternar(id)}
                    className={`cursor-pointer ${marcada ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                    <td className="pl-2">
                      <input type="checkbox" readOnly checked={marcada}
                        aria-label={`Escolher ${b.touro || 'sem touro'} ${b.codigo || ''}`}
                        className="rounded border-slate-300" />
                    </td>
                    <td className="px-2 py-1.5 font-medium text-slate-800">{b.touro || 'sem touro'}</td>
                    <td className="px-2 py-1.5 text-slate-600">{b.codigo || '—'}</td>
                    {!dono && <td className="px-2 py-1.5 text-xs text-slate-600">{b.dono || '—'}</td>}
                    <td className="px-2 py-1.5 text-xs text-slate-500">{enderecoDoBotijao(b) || '—'}</td>
                    <td className="px-2 py-1.5 text-right text-slate-600">{emDoses(b.saldo_doses)}</td>
                    <td className="px-2 py-1.5 text-center" onClick={e => marcada && e.stopPropagation()}>
                      {marcada ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <input type="number" min="0.5" step="0.5"
                            aria-label={`Doses de ${b.touro || 'sem touro'} ${b.codigo || ''}`}
                            className="w-14 px-1 py-0.5 border border-slate-300 rounded text-center bg-white
                                       focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={sel[id] ?? ''}
                            onChange={e => mudarDoses(id, parseFloat(e.target.value) || 0)}
                          />
                          <span className={restante < 0 ? 'font-semibold' : 'text-slate-500'}>
                            {restante < 0 ? `${MARK.WARN} faltam ${emDoses(-restante)}` : `restam ${emDoses(restante)}`}
                          </span>
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex gap-2 p-4 border-t">
        <button onClick={usar} disabled={escolhidas.length === 0}
          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {escolhidas.length === 0
            ? 'Escolha as palhetas'
            : `Usar ${escolhidas.length} partida(s) · ${emDoses(totalDeDoses)} dose(s)`}
        </button>
        <button onClick={onFechar}
          className="px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50">
          Cancelar
        </button>
      </div>
    </JanelaModal>
  );
};

export default SeletorDePalhetas;
